import { useEffect, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { createDockview, themeDark, themeLight, type DockviewApi, type DockviewTheme, type IContentRenderer } from 'dockview';
import 'dockview/dist/styles/dockview.css';
import type { DockContribution } from '../../engine/api';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { useRuntime, useUi, useUiContext } from '../context';
import type { Theme } from '../theme/theme';
import { selectTheme } from '../uiStore';
import { ControlsPanel } from '../panels/controls/ControlsPanel';
import { MixerPanel } from '../panels/mixer/MixerPanel';
import { ScenesPanel } from '../panels/scenes/ScenesPanel';
import { SourcesPanel } from '../panels/sources/SourcesPanel';
import { StatsPanel } from '../panels/stats/StatsPanel';
import { TransitionsPanel } from '../panels/transitions/TransitionsPanel';
import { HealthPanel } from '../health/HealthPanel';
import { StreamManagerPanel } from '../panels/stream-manager/StreamManagerPanel';
import { AdOffersPanel } from '../panels/ad-offers/AdOffersPanel';
import { PreviewPanel } from '../preview/PreviewPanel';
import { errorMessage } from '../safe';
import {
  applyDefaultLayout,
  browserStorage,
  BUILTIN_PANELS,
  clearSavedLayout,
  openPanel,
  PLUGIN_PANEL_PREFIX,
  readSavedLayout,
  readSeenDocks,
  saveLayout,
  writeSeenDocks,
} from './layoutModel';
import '../theme/dock.css';

const COMPONENTS: Record<string, () => JSX.Element> = {
  preview: () => <PreviewPanel />,
  scenes: () => <ScenesPanel />,
  sources: () => <SourcesPanel />,
  mixer: () => <MixerPanel />,
  transitions: () => <TransitionsPanel />,
  controls: () => <ControlsPanel />,
  stats: () => <StatsPanel />,
  health: () => <HealthPanel />,
  'stream-manager': () => <StreamManagerPanel />,
  'ad-offers': () => <AdOffersPanel />,
};

/** Hosts a dock contributed at runtime: the contribution renders into our element and hands back its cleanup. */
function ContributedDock({ dock }: { dock: DockContribution }): JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const node = host.current;
    if (!node) return;
    let cleanup: (() => void) | undefined;
    try {
      cleanup = dock.mount(node);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
    return () => {
      try {
        cleanup?.();
      } finally {
        node.replaceChildren();
      }
    };
  }, [dock]);
  return (
    <>
      {error ? <div className="ui-empty ui-error-text">This dock failed to start: {error}</div> : null}
      <div ref={host} className="studio-dock-host" />
    </>
  );
}

/** dockview's built-in theme for ours; dock.css maps both onto the `--ui-*` tokens. */
export function dockviewThemeFor(theme: Theme): DockviewTheme {
  return theme === 'light' ? themeLight : themeDark;
}

interface MountedPanel {
  id: string;
  element: HTMLElement;
}

/** The dockable workspace. Panels are React portals into elements owned by dockview. */
export function DockLayout(): JSX.Element {
  const { ui, studio } = useUiContext();
  const container = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState<MountedPanel[]>([]);
  const [api, setApi] = useState<DockviewApi | null>(null);
  const docks = useRuntime((r) => r.docks);
  const docksRef = useRef(docks);
  docksRef.current = docks;
  const theme = useUi(selectTheme);
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    const node = container.current;
    if (!node) return;
    const storage = browserStorage();

    const dockview = createDockview(node, {
      theme: dockviewThemeFor(themeRef.current),
      createComponent: ({ id }): IContentRenderer => {
        const element = document.createElement('div');
        element.className = 'studio-dock-panel';
        return {
          element,
          init: () => setMounted((prev) => [...prev.filter((p) => p.id !== id), { id, element }]),
          dispose: () => setMounted((prev) => prev.filter((p) => p.element !== element)),
        };
      },
    });

    const restore = (): void => {
      const saved = readSavedLayout(storage);
      if (saved) {
        try {
          dockview.fromJSON(saved);
          if (dockview.panels.length > 0) return;
        } catch {
          clearSavedLayout(storage);
        }
      }
      applyDefaultLayout(dockview);
    };
    restore();

    let saveTimer: ReturnType<typeof setTimeout> | undefined;
    const layoutSub = dockview.onDidLayoutChange(() => {
      ui.getState().bumpDock();
      clearTimeout(saveTimer);
      saveTimer = setTimeout(() => saveLayout(storage, dockview.toJSON()), 300);
    });

    const specFor = (id: string) => {
      const builtin = BUILTIN_PANELS.find((p) => p.id === id);
      if (builtin) return builtin;
      const dock = docksRef.current.find((d) => PLUGIN_PANEL_PREFIX + d.id === id);
      return dock ? { id, title: dock.title } : null;
    };

    ui.getState().setDock({
      isOpen: (id) => dockview.getPanel(id) !== undefined,
      toggle: (id) => {
        const panel = dockview.getPanel(id);
        if (panel) panel.api.close();
        else {
          const spec = specFor(id);
          if (spec) openPanel(dockview, spec, id);
        }
      },
      show: (id) => {
        if (!dockview.getPanel(id)) {
          const spec = specFor(id);
          if (spec) openPanel(dockview, spec, id);
        }
        dockview.getPanel(id)?.api.setActive();
      },
      reset: () => {
        clearSavedLayout(storage);
        applyDefaultLayout(dockview);
        for (const dock of docksRef.current) {
          const id = PLUGIN_PANEL_PREFIX + dock.id;
          openPanel(dockview, { id, title: dock.title }, id);
        }
      },
    });
    setApi(dockview);

    return () => {
      clearTimeout(saveTimer);
      layoutSub.dispose();
      ui.getState().setDock(null);
      setApi(null);
      dockview.dispose();
    };
  }, [ui]);

  useEffect(() => {
    api?.updateOptions({ theme: dockviewThemeFor(theme) });
  }, [api, theme]);

  // Open a contributed dock the first time it is ever seen; afterwards the saved layout decides.
  // A dock whose plugin is unloaded during the session is closed and forgotten, so loading the plugin
  // again brings it back. (At start-up the layout may list docks of plugins that are still loading;
  // those keep their place.)
  const knownDocks = useRef<readonly string[]>([]);
  useEffect(() => {
    if (!api) return;
    const storage = browserStorage();
    const seen = readSeenDocks(storage);
    let changed = false;
    const present = docks.map((dock) => PLUGIN_PANEL_PREFIX + dock.id);
    for (const id of knownDocks.current) {
      if (present.includes(id)) continue;
      api.getPanel(id)?.api.close();
      changed = seen.delete(id) || changed;
    }
    knownDocks.current = present;
    for (const dock of docks) {
      const id = PLUGIN_PANEL_PREFIX + dock.id;
      if (seen.has(id)) continue;
      seen.add(id);
      changed = true;
      openPanel(api, { id, title: dock.title }, id);
    }
    if (changed) writeSeenDocks(storage, seen);
    ui.getState().bumpDock();
  }, [api, docks, ui]);

  const content = (id: string): JSX.Element => {
    const builtin = COMPONENTS[id];
    if (builtin) return builtin();
    const dock = docks.find((d) => PLUGIN_PANEL_PREFIX + d.id === id);
    if (dock) return <ContributedDock dock={dock} />;
    return <div className="ui-empty">This dock belongs to a plugin that is not loaded.</div>;
  };

  const titleOf = (id: string): string =>
    BUILTIN_PANELS.find((p) => p.id === id)?.title ?? docks.find((d) => PLUGIN_PANEL_PREFIX + d.id === id)?.title ?? 'Dock';

  return (
    <div className="studio-dock">
      <div ref={container} style={{ position: 'absolute', inset: 0 }} />
      {mounted.map((panel) =>
        createPortal(
          <ErrorBoundary name={titleOf(panel.id)} onError={(error) => studio.notify('error', `${titleOf(panel.id)}: ${errorMessage(error)}`)}>
            {content(panel.id)}
          </ErrorBoundary>,
          panel.element,
          panel.id,
        ),
      )}
    </div>
  );
}
